import { AxiosResponse } from 'axios';
import httpClient from './httpClient';
import {
  GithubInstallation,
  GithubMappableWorkspace,
  GithubRepositoryMapping,
  GithubSpaceRepositoryOptions,
  GithubStatusRule,
  GithubStatusRuleInput,
  PendingInstallation,
  TaskPullRequest,
} from '../types/Github';

const GITHUB_BASE_ROUTE = '/v1/integrations/github';

/**
 * Read a parked pending installation without spending it. Not organization-scoped: the
 * user has not chosen an organization yet.
 */
export async function getPendingInstallation(
  token: string
): Promise<AxiosResponse<{ pendingInstallation: PendingInstallation }>> {
  return httpClient({
    method: 'GET',
    url: `${GITHUB_BASE_ROUTE}/pending-installation`,
    params: { token },
  });
}

/**
 * Phase 2 of the install. `organizationId` is the NUMERIC organization id, never the
 * publicId from the URL - the server's organizationGuard reads it as a number.
 */
export async function confirmInstallation(
  organizationId: number,
  pendingToken: string
): Promise<AxiosResponse<{ installation: { installationId: string; accountLogin: string } }>> {
  return httpClient({
    method: 'POST',
    url: `${GITHUB_BASE_ROUTE}/${organizationId}/installations/confirm`,
    data: { pendingToken },
  });
}

export async function createInstallUrl(organizationId: number): Promise<AxiosResponse<{ installUrl: string }>> {
  return httpClient({
    method: 'POST',
    url: `${GITHUB_BASE_ROUTE}/${organizationId}/connect`,
  });
}

export async function getInstallations(
  organizationId: number
): Promise<AxiosResponse<{ installations: GithubInstallation[] }>> {
  return httpClient({
    method: 'GET',
    url: `${GITHUB_BASE_ROUTE}/${organizationId}/installation`,
  });
}

export async function disconnectInstallation(
  organizationId: number,
  installationId: string
): Promise<AxiosResponse<void>> {
  return httpClient({
    method: 'DELETE',
    url: `${GITHUB_BASE_ROUTE}/${organizationId}/installation/${installationId}`,
  });
}

export async function getRepositories(
  organizationId: number
): Promise<AxiosResponse<{ repositories: GithubRepositoryMapping[] }>> {
  return httpClient({
    method: 'GET',
    url: `${GITHUB_BASE_ROUTE}/${organizationId}/repositories`,
  });
}

/** `workspaceId: null` unmaps the repository. */
/** Every workspace in the organization - organization ADMIN only. */
export async function getMappableWorkspaces(
  organizationId: number
): Promise<AxiosResponse<{ workspaces: GithubMappableWorkspace[] }>> {
  return httpClient({
    method: 'GET',
    url: `${GITHUB_BASE_ROUTE}/${organizationId}/mappable-workspaces`,
  });
}

export async function setRepositoryMapping(
  organizationId: number,
  repoId: string,
  workspaceId: number | null
): Promise<AxiosResponse<{ repository: GithubRepositoryMapping }>> {
  return httpClient({
    method: 'PUT',
    url: `${GITHUB_BASE_ROUTE}/${organizationId}/repositories/${repoId}/mapping`,
    data: { workspaceId },
  });
}

/**
 * Space-scoped, not organization-scoped: the route parameter is `spaceId` because that
 * is what the server's `spaceGuard` reads. It accepts both the numeric workspace id and
 * the 22-character public id; pass the numeric one, which is what `Workspace.id` holds.
 *
 * Readable by any space MEMBER. Writing requires OWNER.
 */
export async function getStatusRules(spaceId: number): Promise<AxiosResponse<{ rules: GithubStatusRule[] }>> {
  return httpClient({
    method: 'GET',
    url: `${GITHUB_BASE_ROUTE}/spaces/${spaceId}/status-rules`,
  });
}

/** Sends every rule, not just the changed ones - the endpoint is a batch replace. */
export async function setStatusRules(
  spaceId: number,
  rules: GithubStatusRuleInput[]
): Promise<AxiosResponse<{ rules: GithubStatusRule[] }>> {
  return httpClient({
    method: 'PUT',
    url: `${GITHUB_BASE_ROUTE}/spaces/${spaceId}/status-rules`,
    data: { rules },
  });
}

/**
 * The repositories a space owner may attach to their own space, plus the ones already
 * attached. Space-scoped and OWNER-guarded: a member gets a 403, because a member can
 * attach nothing and the list names private repositories.
 */
export async function getSpaceRepositoryOptions(spaceId: number): Promise<AxiosResponse<GithubSpaceRepositoryOptions>> {
  return httpClient({
    method: 'GET',
    url: `${GITHUB_BASE_ROUTE}/spaces/${spaceId}/repositories`,
  });
}

/**
 * Replace the whole list of repositories attached to a space, so an omitted repoId is a
 * detach. 409s when a repoId is already attached to a different space.
 */
export async function setSpaceRepositories(
  spaceId: number,
  repoIds: string[]
): Promise<AxiosResponse<{ repositories: GithubRepositoryMapping[] }>> {
  return httpClient({
    method: 'PUT',
    url: `${GITHUB_BASE_ROUTE}/spaces/${spaceId}/repositories`,
    data: { repoIds },
  });
}

/**
 * The pull requests linked to one task.
 *
 * NOT under GITHUB_BASE_ROUTE: the server mounts these on `/v1/task`, matching the real
 * task router, because they are authorized as a task sub-resource (requireAuth, load the
 * task, then space access) rather than through an organization or space guard.
 *
 * Readable by anyone with access to the task's space.
 */
export async function getTaskPullRequests(taskId: number): Promise<AxiosResponse<{ pullRequests: TaskPullRequest[] }>> {
  return httpClient({
    method: 'GET',
    url: `/v1/task/${taskId}/pull-requests`,
  });
}
